import { CRAYO_ENTRY, crayoPlan, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const pro = crayoPlan("Pro");

export const EKLIPSE_CONTENT: CompareContent = {
  slug: "crayo-vs-eklipse",
  competitorSlug: "eklipse",
  title: "Crayo vs Eklipse",
  seoTitle: "Crayo vs Eklipse: Pricing, Stream Caps and Features Compared",
  seoDescription:
    "Crayo vs Eklipse with dated prices. Eklipse Premium is $24.99 a month, capped at 3 streams a day, no free tier since June 2026. Crayo meters per finished clip.",
  cardVerdict:
    "Better than Crayo for Twitch and Kick VOD pull and mobile apps, behind on daily caps and formats.",
  bestFor: "Streamers clipping their own VODs",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick for clippers who upload stream VODs or paste YouTube links: it meters per finished clip with no streams-per-day cap, ranks the moments, and puts a timeline editor and a public API on one ${money(hobby.monthly)} plan. Eklipse is the better pick if you need Twitch or Kick VODs pulled in automatically, a live clip-it voice command, console capture or a phone app. Crayo imports neither platform. The evidence is below.`,
  pickCrayoIf: [
    `You clip more than Eklipse's cap allows: Eklipse Premium processes up to 3 streams a day and 45 clips per output, while Crayo Hobby gives ${hobby.credits} credits a month, one per finished clip, with no per-day count.`,
    "You want the moments ranked before you cut: AutoClip returns 1 to 20 ranked clips with a streamer layout for gameplay plus facecam, and Find Viral Moments lists up to 50 ranked moments before a credit is spent.",
    "You want a full timeline editor with a camera track, keyframes, speed ramps and 32 caption styles in 56 spoken languages on every plan, not a vertical template editor, plus a 34-endpoint API.",
  ],
  pickThemIf: [
    "Your streams live on Twitch or Kick and you want the VOD pulled in by OAuth after each stream. Crayo does not import Twitch or Kick VODs; you upload the file or paste a YouTube link.",
    "You stream from a PS5 or Xbox without a capture card, or want to say clip it on stream and have the moment saved. Crayo has neither.",
    "You want iOS and Android apps and a publisher that posts to TikTok, Shorts and Reels. Crayo is a browser app and you post the export yourself.",
  ],
  wins: [
    {
      claim: "Crayo has no streams-per-day cap; Eklipse Premium stops at 3 streams a day.",
      evidence:
        "Eklipse's blog, modified Sep 27, 2026, lists Premium at up to 3 streams a day and 45 clips per output, and its help center caps Twitch and Kick VODs at 6 hours. Crayo meters by finished clip, takes sources up to 3 hours or 4 GB each, and counts no streams.",
      href: "https://blog.eklipse.gg/eklipse-news-and-guide/eklipse-premium-plan.html",
    },
    {
      claim: "Crayo's timeline editor is on every plan; Eklipse Studio is a vertical template editor.",
      evidence:
        "Eklipse's own comparison table, updated July 2026, describes Eklipse Studio as timeline, templates and overlays. Crayo's editor adds a camera track, keyframes, zooms, transitions, speed ramps, motion blur, loudness normalization and 32 caption styles with keyword highlight, and opens any AutoClip clip in it.",
      href: "https://eklipse.gg/compare/eklipse-vs-opusclip/",
    },
    {
      claim: "Eklipse's own review says its detection misses more than it catches on Just Chatting, IRL and MOBA streams.",
      evidence:
        "The statement is on blog.eklipse.gg, which also says VODs can wait hours during peak evenings and weekends. Crayo's AutoClip ranks moments from the transcript and has action and no-talking content modes for gameplay; the scheduled test below will put dated numbers on both tools.",
      href: "https://blog.eklipse.gg/featured/eklipse-gg-review.html",
    },
    {
      claim: "Crayo cancels in one click from Settings; Eklipse does not describe cancellation on its pricing or refund pages.",
      evidence:
        "Eklipse's refund policy, checked Oct 8, 2026, says refunds are generally not provided and does not describe a cancel flow. Crayo's plan stays active to the end of the cycle after a one-click cancel and nothing further is charged.",
      href: "https://crayo.ai/refund-policy",
    },
    {
      claim: "Crayo has a 34-endpoint API with webhooks on every plan; Eklipse publishes no API.",
      evidence:
        "Eklipse's homepage and features, checked Oct 8, 2026, list stream clipping, AI captions, templates and a publisher, with no public API. Crayo's API and webhooks are on the entry plan, with docs at crayo.ai/developers, and the same plan also includes AI voiceover, AI images and Enhance Speech.",
      href: "https://crayo.ai/developers",
    },
  ],
  concessions: [
    {
      claim: "Eklipse pulls Twitch, Kick and YouTube VODs in by OAuth and saves clips from a voice command during a live stream.",
      evidence:
        "Eklipse's about page lists official Twitch, Kick and YouTube integrations, and the homepage shows the clip-it Voice Command. Crayo takes YouTube and TikTok links or a file upload, so a Twitch or Kick streamer downloads the VOD and uploads it, up to 4 GB and 3 hours per source.",
      href: "https://eklipse.gg/about",
    },
    {
      claim: "Eklipse detects game events across 3,000+ games and captures console streams without a capture card.",
      evidence:
        "The about page, read Oct 8, 2026, claims 3,000+ games supported and a Console Streamer mode for PS5 and Xbox. Crayo has no game-event library; its gameplay clips come from the transcript and the action and no-talking modes.",
      href: "https://eklipse.gg/about",
    },
    {
      claim: "Eklipse has iOS and Android apps and a publisher that posts to TikTok, Shorts and Reels.",
      evidence:
        "The iOS app has been listed since April 2023 and the about page cites 100,000+ Google Play installs; the homepage lists the Content Publisher. Crayo is a browser app with no native scheduling, so you download the export and post it.",
      href: "https://eklipse.gg/",
    },
    {
      claim: "Eklipse captures Ultra Highlights at up to 1440p and sells a human Pro Edits service.",
      evidence:
        "The homepage lists 1440p capture and Premium exports at 1080p 60fps; the premium page describes Pro Edits with a 3-day turnaround, English audio only, price not stated. Crayo exports at up to 1080p and has no human editing service.",
      href: "https://eklipse.gg/premium/",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute stream VOD a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes; each 60-minute VOD is under the 3-hour, 4 GB source limit`,
      note: "The VOD is uploaded as a file or pasted as a YouTube link; Crayo does not import Twitch or Kick VODs. Prices from the live plan table.",
    },
    them: {
      plan: "Premium",
      cost: "$24.99/mo, $112.99 per 6 months, or $179.99/yr (about $14.99/mo)",
      usage: "4 streams a month against a cap of 3 streams a day, 8 clips each against 45 per output; a 60-minute VOD is far below the 6-hour limit",
      note: "No public pricing page; figures from Eklipse's help center, updated Apr 12, 2026. $27.99/mo if bought in the mobile apps.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 2-hour gameplay stream VOD with facecam owned by Crayo",
    plans: "Eklipse Premium and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Eklipse has no public pricing page: the Pricing link opens a login-gated register page and the prices live in the help center. Premium is $24.99 a month, $112.99 per 6 months or $179.99 a year on the web and $27.99 a month inside the iOS and Android apps. Refunds are generally not provided, and the help center says there is no free trial.",
  corrections: [
    {
      site: "eklipse.gg",
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claim: "Crayo does not analyze your streams.",
      reality:
        "Not current. AutoClip finds and ranks moments in any uploaded stream VOD up to 3 hours or 4 GB, with a streamer layout that keeps gameplay and facecam in frame, action and no-talking content modes for gameplay, and virality ranking. The VOD comes in as a file upload or a YouTube link; Crayo does not pull from Twitch or Kick by OAuth, and Eklipse does.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "eklipse.gg",
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claim: "Highlight detection: No, gameplay is background b-roll.",
      reality:
        "Not current. AutoClip cuts 1 to 20 ranked clips of 15 to 120 seconds from a gameplay VOD, and Find Viral Moments returns up to 50 ranked moments to review before cutting. Gameplay can also be used as background in Crayo's story-video generator, but that is a separate workflow, not the clipper.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "eklipse.gg",
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claim: "AI voiceover + 15+ caption styles.",
      reality:
        "Half right. Crayo's AI voiceover has hundreds of voices on every plan. The caption count is 32 styles in the editor, with keyword highlight, and transcription in 56 spoken languages.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "Caption styles",
    },
    {
      site: "eklipse.gg",
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claim: "No free tier; paid tiers from about $19/mo.",
      reality:
        `Partly. Crayo has no free plan and no trial, and the figure it quotes was the entry price until October 2026. As of October 25, 2026 the entry plan is Hobby at ${money(hobby.monthly)} a month, or ${money(hobby.quarterlyPerMonth)} a month billed ${money(hobby.quarterlyTotal)} every 3 months, for ${hobby.credits} workflow credits. Twelve free browser tools at crayo.ai/features/free need no account and never upload the file. Eklipse has had no free tier since June 2026 either, per its own help center.`,
      evidenceHref: "https://crayo.ai/features/free",
      evidenceLabel: "Free tools",
    },
    {
      site: "eklipse.gg",
      url: "https://eklipse.gg/compare/eklipse-vs-crayo/",
      date: "2026-07-22",
      claim: "About $13/month billed annually.",
      reality:
        `Stale since October 2026. Hobby is now ${money(hobby.yearlyTotal)} a year, about ${money(hobby.yearlyPerMonth)} a month, or ${money(hobby.quarterlyTotal)} every 3 months on the quarterly plan. The pricing page shows monthly, quarterly and yearly figures.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Pricing",
    },
  ],
  faqs: [
    {
      question: "Is Crayo cheaper than Eklipse?",
      answer:
        `Not on sticker price any more, unless you bill quarterly. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. Eklipse Premium is $24.99 a month or $179.99 a year, so Eklipse is lower on monthly and yearly billing and Crayo's quarterly plan undercuts Eklipse's monthly price. Eklipse is capped by streams, not clips: up to 3 streams a day and 45 clips per output. A streamer who clips one VOD a week fits inside both plans. A streamer who processes more than 3 streams a day cannot do it on Eklipse at any price, while Crayo Pro gives ${pro.credits.toLocaleString("en-US")} credits a month.`,
    },
    {
      question: "Is Eklipse free?",
      answer:
        "No, not any more. Eklipse's help center, updated Aug 13, 2026, says Eklipse is a paid app: a free account gets a preview scan of one recent stream and exporting requires Premium. The old free tier with 720p exports, one stream a day, 15 clips per stream and a watermark ended in June 2026, per Eklipse's blog, and there is no time-limited trial. Crayo also has no free plan, but its twelve free browser tools at crayo.ai/features/free run without an account and never upload your file.",
    },
    {
      question: "How much does Eklipse Premium cost?",
      answer:
        "Eklipse Premium is $24.99 a month, $112.99 per 6 months or $179.99 a year, which is about $14.99 a month, when bought on the web. Inside the iOS and Android apps the monthly price is $27.99. There is no public pricing page; the Pricing link opens a login-gated register page, and these figures come from Eklipse's help center, updated Apr 12, 2026. Premium covers up to 3 streams a day, up to 45 clips per output and 1080p 60fps exports with no watermark.",
    },
    {
      question: "Can Crayo clip Twitch or Kick streams like Eklipse?",
      answer:
        "Only from a file. Crayo's AutoClip takes YouTube and TikTok links or a file upload up to 4 GB and 3 hours, so a Twitch or Kick streamer downloads the VOD and uploads it. Eklipse connects Twitch, Kick and YouTube by OAuth and pulls the VOD in after each stream, which is a real convenience Crayo does not match. Once the VOD is in, Crayo's streamer layout keeps gameplay and facecam in frame and ranks the moments.",
    },
    {
      question: "Does Crayo detect gameplay highlights like Eklipse?",
      answer:
        "Yes, with a different method. Eklipse detects game events across a library of 3,000+ games and admits on its own blog that detection misses more than it catches on Just Chatting, IRL and MOBA streams. Crayo's AutoClip ranks moments from the transcript and offers action and no-talking content modes for gameplay with little speech, returning 1 to 20 ranked clips per run. Neither tool has been tested head to head yet; the scheduled test on a 2-hour facecam VOD will publish dated results.",
    },
    {
      question: "Does Crayo auto-post to TikTok like Eklipse?",
      answer:
        "No. Crayo exports the file at up to 1080p and you post it yourself. Eklipse's Content Publisher posts to TikTok, YouTube Shorts and Instagram Reels, and its iOS and Android apps let you do that from a phone. If posting from the clipping tool is the deciding feature, Eklipse wins that row. Crayo's View Tracker follows Instagram, YouTube and TikTok creators for cadence and viral outliers, but it is not a scheduler.",
    },
    {
      question: "Does Eklipse give refunds?",
      answer:
        "Eklipse's refund policy says refunds are generally not provided for any fees already paid, checked Oct 8, 2026. Crayo's policy is the same: all sales are final. The difference is in cancelling. Crayo cancels in one click from Settings, the plan stays active to the end of the cycle and nothing further is charged. Eklipse's pricing help pages and refund policy do not describe how to cancel.",
    },
    {
      question: "Which is better for streamers, Crayo or Eklipse?",
      answer:
        "It depends on where the VOD comes from. If you stream on Twitch or Kick and want the VOD pulled in automatically, clip-it voice commands on stream, console capture or a phone app, Eklipse is built for that. If you upload the VOD file or clip YouTube videos, want the moments ranked before you cut, a timeline editor with a camera track and 32 caption styles, or need an API, Crayo covers more on one plan without a daily stream cap. Both have no free tier and no refunds.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Eklipse prices checked Oct 8, 2026 from its help center (no public pricing page): Premium $24.99/mo, $112.99 per 6 months, $179.99/yr, $27.99/mo in the mobile apps; free tier ended June 2026.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-streamladder", "crayo-vs-opus-clip", "crayo-vs-capcut"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "eklipse alternative",
    "eklipse vs crayo",
    "crayo vs eklipse",
    "eklipse pricing",
    "eklipse free",
    "is eklipse free",
    "best ai clip maker for streamers",
  ],
};
